import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, GripVertical, Building, Megaphone, Printer, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import ImageUploader from '../../components/ImageUploader';
import { resolveImageUrl } from '../../lib/imageUrl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = [
  { value: 'building', label: 'Bâtiment', icon: Building },
  { value: 'megaphone', label: 'Communication', icon: Megaphone },
  { value: 'printer', label: 'Impression', icon: Printer },
  { value: 'package', label: 'Équipement', icon: Package },
];

export default function AdminServices() {
  const { t, getLocalizedContent } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: { fr: '', en: '' },
    description: { fr: '', en: '' },
    short_description: { fr: '', en: '' },
    icon: 'building',
    image_url: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?active_only=false`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axios.put(`${API}/admin/services/${editingService.id}`, formData);
        toast.success('Service mis à jour');
      } else {
        await axios.post(`${API}/admin/services`, formData);
        toast.success('Service créé');
      }
      setShowModal(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`);
      toast.success('Service supprimé');
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title || { fr: '', en: '' },
      description: service.description || { fr: '', en: '' },
      short_description: service.short_description || { fr: '', en: '' },
      icon: service.icon || 'building',
      image_url: service.image_url || '',
      order: service.order || 0,
      is_active: service.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      title: { fr: '', en: '' },
      description: { fr: '', en: '' },
      short_description: { fr: '', en: '' },
      icon: 'building',
      image_url: '',
      order: 0,
      is_active: true
    });
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">
          {t('admin_services')}
        </h1>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
          data-testid="add-service-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('admin_new')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {services.map((service, index) => {
            const IconComp = iconOptions.find(i => i.value === service.icon)?.icon || Building;
            return (
              <Card key={service.id} className="border-0 shadow-sm" data-testid={`service-item-${index}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <GripVertical className="h-5 w-5 text-gray-400 cursor-grab" />
                  <div className="w-12 h-12 bg-[#0B6AB3]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComp className="h-6 w-6 text-[#0B6AB3]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0F172A]">{getLocalizedContent(service.title)}</h3>
                    <p className="text-sm text-gray-500 truncate">{getLocalizedContent(service.short_description)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${service.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {service.is_active ? 'Actif' : 'Inactif'}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(service)} data-testid={`edit-service-${index}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} className="text-red-500" data-testid={`delete-service-${index}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="service-modal">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Modifier le service' : 'Nouveau service'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Titre (FR) *</Label>
                <Input
                  value={formData.title.fr}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, fr: e.target.value } })}
                  required
                  data-testid="service-title-fr"
                />
              </div>
              <div>
                <Label>Titre (EN)</Label>
                <Input
                  value={formData.title.en}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                  data-testid="service-title-en"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Description courte (FR) *</Label>
                <Textarea
                  value={formData.short_description.fr}
                  onChange={(e) => setFormData({ ...formData, short_description: { ...formData.short_description, fr: e.target.value } })}
                  rows={2}
                  required
                />
              </div>
              <div>
                <Label>Description courte (EN)</Label>
                <Textarea
                  value={formData.short_description.en}
                  onChange={(e) => setFormData({ ...formData, short_description: { ...formData.short_description, en: e.target.value } })}
                  rows={2}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Description complète (FR) *</Label>
                <Textarea
                  value={formData.description.fr}
                  onChange={(e) => setFormData({ ...formData, description: { ...formData.description, fr: e.target.value } })}
                  rows={4}
                  required
                />
              </div>
              <div>
                <Label>Description complète (EN)</Label>
                <Textarea
                  value={formData.description.en}
                  onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                  rows={4}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Icône</Label>
                <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <ImageUploader
                label="Image du service"
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                testIdPrefix="service-image"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
              <Label>Service actif</Label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                {t('common_cancel')}
              </Button>
              <Button type="submit" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white" data-testid="save-service-btn">
                {t('common_save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
